import { Button, Spinner } from "flowbite-react";
import { YMButton, YMTable } from "../../ym-components";
import React, { useContext, useEffect, useState } from "react";
import { useProjectFewShotLearningModels } from "./Model";
import { useFewShotTaggingModelApi } from "./Api";
import { NewModelForm } from "./components/NewModel";
import readXlsxFile from "read-excel-file";
import { notificationError, notificationInfo } from "../../toast-notification";
import { AiOutlineDownload } from "react-icons/ai";
import { IoReloadOutline } from "react-icons/io5";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { FewShotTaggingModelStatus } from "../../app_client";
import { HiTrash, HiPlus, HiPencil } from "react-icons/hi";
import { BsFillGearFill } from "react-icons/bs";
import ModelFilter from "./ModelFilter";
import { TagManager, WorkspaceTag } from "../../app_client/components";
import { WorkspaceTagType, useWorkspaceApi } from "../workspace/Api";
import { AuthUserContext } from "../../AuthUserContext";

export default function FewShotLearningModels(props: any) {
  const [displayAllClass, setDisplayAllClass] = React.useState("");
  const navigate = useNavigate();
  const [filterName, setFilterName] = useState("");
  const [filterTagIds, setFilterTagIds] = useState<string[]>([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [selectedModelForTags, setSelectedModelForTags] = useState<any | null>(null);
  const [allTags, setAllTags] = useState<WorkspaceTagType[]>([]);

  const api = useFewShotTaggingModelApi();
  const workspaceApi = useWorkspaceApi();
  const { workspaceId } = useContext(AuthUserContext) as {
    workspaceId: string;
  };

  // Fetch workspace tags
  useEffect(() => {
    if (workspaceApi && workspaceId) {
      workspaceApi.getWorkspaceTags(workspaceId).then(setAllTags);
    }
  }, [workspaceApi, workspaceId]);

  const model = useProjectFewShotLearningModels({
    api,
    filterName,
    filterTagIds,
    readXLSX: async (file: File) => {
      let data = await readXlsxFile(file);
      return data.map((r) => r.map((c) => `${c}`));
    },
    notifyError: (message: string) => {
      notificationError(message);
    },
    notifyInfo: (message: string) => {
      notificationInfo(message);
    },
  });
  let { _models } = model;

  const handleOpenTagManager = (m: any) => {
    setSelectedModelForTags(m);
    setShowTagManager(true);
  };

  const handleCloseTagManager = () => {
    setShowTagManager(false);
    setSelectedModelForTags(null);
  };

  const handleCreateTag = async (name: string, color: string): Promise<string> => {
    if (!workspaceApi || !workspaceId) {
      return Promise.reject("Workspace API not available");
    }

    const newTag = await workspaceApi.createWorkspaceTag(workspaceId, name, color);
    setAllTags([...allTags, newTag]);
    return newTag.id || newTag.name;
  };

  const handleSaveTags = async (tagNames: string[]): Promise<void> => {
    if (!selectedModelForTags) {
      return Promise.reject("No model selected");
    }

    if (!workspaceApi || !workspaceId) {
      return Promise.reject("Workspace API not available");
    }

    // Get tag IDs from tag names
    const tagIds = tagNames
      .map((name) => {
        const tag = allTags.find((t) => t.name === name);
        return tag?.id || tag?.name;
      })
      .filter((id): id is string => id !== undefined);

    // Call API to assign tags to model
    await workspaceApi.assignTagsToModel(workspaceId, selectedModelForTags.model_id, tagIds);

    // Update the model with the new tags in local state
    const updatedTags = tagNames.map((name) => {
      const tag = allTags.find((t) => t.name === name);
      return tag || { name, color: "gray" };
    });

    // Refetch models to get updated tags
    _models.refetch();

    return Promise.resolve();
  };

  return (
    <div>
      <NewModelForm {...model} />
      <div className="mt-5">
        <ModelFilter
          onSearch={setFilterName}
          onTagsChange={setFilterTagIds}
          workspaceId={workspaceId}
          availableTags={allTags}
        />
      </div>
      <div>
        {_models.isInitialLoading ? (
          <div className="w-100 mt-10 text-center">
            <Spinner
              color="gray"
              size="xl"
              aria-label="Default status example"
            />
            <div className="mt-4 text-gray-400">Fetching models</div>
          </div>
        ) : model.state.WorkspaceModelsComputed.length === 0 ? (
          <div className="w-100 mt-5 text-center">
            <p className="text-gray-500 mb-4">
              No fewshot tagging models available.
            </p>
          </div>
        ) : (
          <div className=" overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-lg mt-5">
            <YMTable
              header={
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Model
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Tags
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Classes d'entrainement
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Type de classification
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Modèle d'embedding
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Créé le
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Lien Beam
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <span className="sr-only"> x </span>
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <span className="sr-only"> x </span>
                  </th>
                </tr>
              }
              rows={model.state.WorkspaceModelsComputed.map((m) => (
                <tr
                  key={m.model_id}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100"
                >
                  <td className="px-6 py-4 text-indigo-900 cursor-pointer capitalize font-bold max-w-[48px] hover:text-indigo-900 dark:text-white">
                    {m.name} <br />
                    <span className={"font-normal text-xs"}>
                      {" "}
                      {statusName(m.status)}{" "}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      {m.tags?.map((tag: WorkspaceTagType) => (
                        <WorkspaceTag key={tag.name} tag={tag} />
                      ))}
                      <Button
                        size="xs"
                        color="indigo"
                        onClick={() => handleOpenTagManager(m)}
                        title="Manage tags"
                      >
                        <HiPencil className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                  <td className="px-6 py-4 max-w-[48px]">
                    {Object.entries(m.training_example_counter || {})
                      .slice(0, m.model_id != displayAllClass ? 5 : undefined)
                      .map(([k, v]) => (
                        <div key={k}>
                          <span className="text-indigo-900 capitalize font-bold">
                            {k}&nbsp;
                          </span>
                          <span className="text-indigo-800 text-xs">{v}</span>
                        </div>
                      ))}
                    {Object.entries(m.training_example_counter || {}).length >
                      5 &&
                      displayAllClass !== m.model_id && (
                        <a
                          className={"text-indigo-900 cursor-pointer"}
                          onClick={() => setDisplayAllClass(m.model_id)}
                        >
                          voir toutes les classes ...{" "}
                        </a>
                      )}
                    {displayAllClass === m.model_id && (
                      <a
                        className={"text-indigo-900 cursor-pointer"}
                        onClick={() => setDisplayAllClass("")}
                      >
                        cacher ...{" "}
                      </a>
                    )}
                  </td>
                  <td className="px-6 py-4">{m.classification_method}</td>
                  <td className="px-6 py-4">{m.embedding_model}</td>
                  <td className="px-6 py-4">
                    {m.created_at &&
                      DateTime.fromISO(m.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    {m.remote_task_monitoring_url && (
                      <a
                        href={m.remote_task_monitoring_url}
                        target="_blank"
                        className="text-blue-600"
                        rel="noreferrer"
                      >
                        Voir sur Beam
                      </a>
                    )}
                  </td>
                  <td className={"px-6 py-4"}>
                    <YMButton
                      text={"Données d'entraînement"}
                      icon={<AiOutlineDownload className={"h-6 w-6 pr-2"} />}
                      size={"xs"}
                      color={"white"}
                      onClick={() => model.actions.onDownloadTrainingData(m)}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className={"flex flex-row"}>
                      <Button
                        size="xs"
                        color="gray"
                        onClick={() => model.actions.triggerModelTraining(m)}
                      >
                        <IoReloadOutline className="h-4 w-4" /> Entrainement
                      </Button>
                      <Button
                        size="xs"
                        color="gray"
                        onClick={() => model.actions.triggerModelEvaluate(m)}
                      >
                        <IoReloadOutline className="h-4 w-4" /> Evaluation
                      </Button>
                      <Button
                        size="xs"
                        color="gray"
                        onClick={() =>
                          navigate(
                            `/few_shot_learning_models/edit?id=${m.model_id}&name=${m.name}`
                          )
                        }
                      >
                        <BsFillGearFill className="h-4 w-4" />
                      </Button>
                      <Button
                        size="xs"
                        color="gray"
                        onClick={() => model.actions.disableModel(m)}
                      >
                        <HiTrash className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            />
          </div>
        )}
      </div>
      {showTagManager && selectedModelForTags && (
        <TagManager
          show={showTagManager}
          onClose={handleCloseTagManager}
          existingTags={allTags}
          selectedTags={selectedModelForTags.tags || []}
          onCreateTag={handleCreateTag}
          onSaveTags={handleSaveTags}
        />
      )}
    </div>
  );
}

const statusName = (status: FewShotTaggingModelStatus) => {
    switch (status) {
        case FewShotTaggingModelStatus.READY:
            return "";
        case FewShotTaggingModelStatus.TRAINING:
            return "Entraînement en cours...";
        case FewShotTaggingModelStatus.EMBEDDING_TRAIN_DATA:
            return "Embedding en cours...";
        case FewShotTaggingModelStatus.TRAINING_REMOTELY:
            return "Entraînement en cours sur beam...";
        default:
            return status;
    }
}